import { useNavigate } from '@tanstack/react-router';
import styled from 'styled-components';

import {
    DemandInformationOfferPort,
    DemandInformationVessel,
    VesselState,
} from '../../../types/DemandInformationMessage';
import { CategoryState } from '../../../types/ReachInformationMessage';
import { DiscoverableButton } from '../../cdl/Button/DiscoverableButton';
import { Link } from '../../cdl/Link/Link';
import { Tag } from '../../cdl/Tag/Tag';
import { Tooltip } from '../../cdl/Tooltip/Tooltip';
import { formatPort } from '../../common/helpers/formatPort.helper';
import { translate } from '../../common/helpers/translate.helper';
import { useRole } from '../../common/hooks/useRole';
import { Box } from '../../common/ui/Box';
import { getMatchingDemandOffer } from '../utils/getMatchingDemandOffer';

import { DemandAdditionalActions } from './DemandAdditionalActions';
import { DemandHeaderWrapper } from './DemandHeaderWrapper';
import { DemandVesselDaysLeftSailing } from './DemandVesselDaysLeftSailing';
import { VesselScheduleSummary } from './VesselScheduleSummary/VesselScheduleSummary';

const getOrderedPortTooltipLabel = (vessel: DemandInformationVessel) => {
    const ports = vessel.categories.length
        ? vessel.categories.map((row) => getMatchingDemandOffer(row, vessel)?.port)
        : vessel.offers.map((offer) => offer.port);
    const portLabels = Array.from(
        new Map(
            ports
                .filter((port): port is DemandInformationOfferPort => Boolean(port))
                .map((port) => [
                    port.id,
                    formatPort({ port: { ...port, locCode: port.locode }, country: false }) ?? port.name,
                ])
        ).values()
    );

    if (portLabels.length === 1) {
        return portLabels[0];
    }

    if (portLabels.length === 2) {
        return portLabels.join(', ');
    }

    if (portLabels.length > 2) {
        return translate('demand.multiplePorts');
    }

    return null;
};

interface VesselStateBadgeProps {
    state: VesselState;
    tooltip: string | null;
}

const VesselStateBadge = ({ state, tooltip }: VesselStateBadgeProps) => {
    switch (state) {
        case VesselState.ORDERED:
            return tooltip ? (
                <Tooltip label={tooltip} showArrow>
                    <span>
                        <Tag variant="default">{translate('demand.ordered')}</Tag>
                    </span>
                </Tooltip>
            ) : (
                <Tag variant="default">{translate('demand.ordered')}</Tag>
            );
        case VesselState.PLANNED:
            return <Tag variant="default">{translate('demand.planned')}</Tag>;
        case VesselState.OPEN:
            return null;
    }
};

const CategoryStateBadge = ({ state }: { state: CategoryState | null }) => {
    switch (state) {
        case CategoryState.WARNING_LIMIT:
            return <Tag variant="attention">{translate('demand.warningLimit')}</Tag>;
        case CategoryState.SAFETY_RESERVE:
            return (
                <Tag variant="attention" emphasis>
                    {translate('demand.onSafetyReserve')}
                </Tag>
            );
        default:
            return null;
    }
};

const VesselNameContainer = styled.div`
    width: 100%;
    min-width: 0;
`;

const VesselNameLink = styled(Link)`
    display: block;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

interface DemandAccordionHeaderProps {
    isOpen: boolean;
    disabled: boolean;
    vessel: DemandInformationVessel;
    onClick: () => void;
}

export const DemandAccordionHeader = ({ isOpen, disabled, onClick, vessel }: DemandAccordionHeaderProps) => {
    const navigate = useNavigate();
    const role = useRole();

    const handleHeaderClick = () => {
        if (disabled) {
            return;
        }

        onClick();
    };

    const vesselDetailPageLink = '/vessel/$id';
    const categoryStateToShow =
        vessel.categories.find((category) => category.categoryState === CategoryState.SAFETY_RESERVE)?.categoryState ??
        vessel.categories.find((category) => category.categoryState === CategoryState.WARNING_LIMIT)?.categoryState ??
        null;
    const orderedPortTooltipLabel = getOrderedPortTooltipLabel(vessel);

    return (
        <DemandHeaderWrapper isOpen={isOpen} disabled={disabled} onClick={handleHeaderClick}>
            <VesselNameContainer>
                <VesselNameLink
                    size="small"
                    to={vesselDetailPageLink}
                    params={{
                        id: vessel.id,
                    }}
                >
                    {vessel.name}
                </VesselNameLink>
            </VesselNameContainer>

            <div>
                <DemandVesselDaysLeftSailing categories={vessel.categories} />
            </div>
            <Box display="flex" gap={8}>
                <CategoryStateBadge state={categoryStateToShow} />
                <VesselStateBadge state={vessel.state} tooltip={orderedPortTooltipLabel} />
            </Box>
            <VesselScheduleSummary vessel={vessel} />
            <Box marginLeft="auto" display="flex" alignItems="center" gap={8}>
                <DiscoverableButton
                    emphasis="high"
                    onClick={(event) => {
                        navigate({
                            to: vesselDetailPageLink,
                            params: {
                                id: vessel.id,
                            },
                        });
                        event.stopPropagation();
                    }}
                >
                    {translate('demand.vesselDetailsButton')}
                </DiscoverableButton>
                {!role.isSuperAdmin() && role.hasWriteRights(vessel.customerId) ? (
                    <DemandAdditionalActions vessel={vessel} />
                ) : null}
            </Box>
        </DemandHeaderWrapper>
    );
};
